import { _cs } from "@togglecorp/fujs";
import { Icon as ReactIconsKitIcon } from "react-icons-kit";
import { angleDown } from "react-icons-kit/fa/angleDown";
import { angleUp } from "react-icons-kit/fa/angleUp";
import { arrowsAlt } from "react-icons-kit/fa/arrowsAlt";
import { caretDown } from "react-icons-kit/fa/caretDown";
import { caretRight } from "react-icons-kit/fa/caretRight";
import { chevronDown } from "react-icons-kit/fa/chevronDown";
import { chevronUp } from "react-icons-kit/fa/chevronUp";
import { circle } from "react-icons-kit/fa/circle";
import { circleONotch } from "react-icons-kit/fa/circleONotch";
import { cloud } from "react-icons-kit/fa/cloud";
import { comments } from "react-icons-kit/fa/comments";
import { download } from "react-icons-kit/fa/download";
import { filePdfO } from "react-icons-kit/fa/filePdfO";
import { graduationCap } from "react-icons-kit/fa/graduationCap";
import { home } from "react-icons-kit/fa/home";
import { infoCircle } from "react-icons-kit/fa/infoCircle";
import { language } from "react-icons-kit/fa/language";
import { lineChart } from "react-icons-kit/fa/lineChart";
import { listUl } from "react-icons-kit/fa/listUl";
import { phone } from "react-icons-kit/fa/phone";
import { plus } from "react-icons-kit/fa/plus";
import { search } from "react-icons-kit/fa/search";
import { sliders } from "react-icons-kit/fa/sliders";
import { sort } from "react-icons-kit/fa/sort";
import { sortAsc } from "react-icons-kit/fa/sortAsc";
import { sortDesc } from "react-icons-kit/fa/sortDesc";
import { square } from "react-icons-kit/fa/square";
import { table } from "react-icons-kit/fa/table";
import { times } from "react-icons-kit/fa/times";
import { unlockAlt } from "react-icons-kit/fa/unlockAlt";
import React from "react";

import iconNames from "../../../constants/iconNames";
import imagePaths from "../../../constants/imagePaths";
import ScalableVectorGraphics from "../../View/ScalableVectorGraphics";

import EarthquakeChart from "#resources/icons/Earthquake-Charts.svg";
import RiskInfoIcon from "#resources/icons/RiskInfo.svg";
import styles from "./styles.module.scss";

// === Icon Registry Types ===
type IconType = "font" | "svg" | "image";

interface BaseIcon {
  name: string;
  type: IconType;
}

interface FontIcon extends BaseIcon {
  type: "font";
  value: string;
}

interface SvgIcon extends BaseIcon {
  type: "svg";
  value: string;
}

interface ImageIcon extends BaseIcon {
  type: "image";
  value: string;
}

type IconEntry = FontIcon | SvgIcon | ImageIcon;

const iconMapping: Record<string, IconEntry> = {};
const fontAwesomeSvgIcons: Record<string, unknown> = {
  "angle-down": angleDown,
  "angle-up": angleUp,
  "arrows-alt": arrowsAlt,
  "caret-down": caretDown,
  "caret-right": caretRight,
  "chevron-down": chevronDown,
  "chevron-up": chevronUp,
  circle,
  "circle-o-notch": circleONotch,
  cloud,
  comments,
  download,
  "file-pdf-o": filePdfO,
  "graduation-cap": graduationCap,
  home,
  "info-circle": infoCircle,
  language,
  "line-chart": lineChart,
  "list-ul": listUl,
  phone,
  plus,
  search,
  sliders,
  sort,
  "sort-asc": sortAsc,
  "sort-desc": sortDesc,
  square,
  table,
  times,
  "unlock-alt": unlockAlt,
};

function getFontAwesomeSvgIcon(classNames: string) {
  const fontAwesomeClass = classNames
    .split(/\s+/)
    .find((className) => className.startsWith("fa-"));

  if (!fontAwesomeClass) {
    return undefined;
  }

  return fontAwesomeSvgIcons[fontAwesomeClass.slice(3)];
}

export function addIcon(type: IconType, name: string, value: string): void {
  iconMapping[name] = { name, type, value } as IconEntry;
}

Object.entries(iconNames).forEach(([key, value]) => {
  addIcon("font", key, value as string);
});

Object.entries(imagePaths).forEach(([key, value]) => {
  addIcon("image", key, value as string);
});

addIcon("svg", "riskInfoSvg", RiskInfoIcon);
addIcon("svg", "earthquakeCharts", EarthquakeChart);

interface IconProps extends React.HTMLAttributes<HTMLElement> {
  className?: string;
  name?: string;
}

const Icon: React.FC<IconProps> = ({ className, name, ...rest }) => {
  const icon = name ? iconMapping[name] : undefined;

  if (!icon) {
    if (name !== undefined) {
      console.warn(`[Icon] No icon found for name: "${name}"`);
    }
    return null;
  }
  console.log(icon.value, icon, "hey");
  switch (icon.type) {
    case "font": {
      const fontAwesomeSvgIcon = getFontAwesomeSvgIcon(icon.value);

      if (fontAwesomeSvgIcon) {
        return (
          <ReactIconsKitIcon
            {...rest}
            className={_cs(
              className,
              styles.fontSvg,
              icon.value.includes("fa-spin") && styles.spin
            )}
            icon={fontAwesomeSvgIcon}
            size="1em"
            tag="span"
          />
        );
      }

      return <span {...rest} className={_cs(className, icon.value)} />;
    }

    case "svg":
      return (
        <ScalableVectorGraphics
          {...(rest as React.SVGAttributes<SVGSVGElement>)}
          className={_cs(className, styles.svg)}
          src={icon.value}
        />
      );

    case "image":
      return (
        <img
          {...(rest as React.ImgHTMLAttributes<HTMLImageElement>)}
          alt={icon.name}
          className={_cs(className, styles.image)}
          src={icon.value}
        />
      );

    default:
      return null;
  }
};

export default Icon;
